/** The sandbox's tester allowlist (`config.sandbox.outbound.allowlist`, SBX D5 / O19): e-mail domains,
 *  exact e-mail addresses and exact phone numbers. Shared by the Sandbox tab's outbound editor, "Add
 *  sandbox" and the create wizard.
 *
 *  Each list is a chip input: type or paste (commas, spaces and new lines separate entries), Enter or
 *  leaving the field adds what was typed. The server normalizes and refuses with a reason per entry
 *  (public mailbox domains, reserved/test domains, phones that are not E.164); our own domains are on
 *  every list implicitly and never stored, so they are shown, not editable. */
import { Autocomplete, Box, Chip, TextField } from '@mui/material';

import { ConsoleApiError } from '@/api/client';
import type { SandboxAllowlist } from '@/api/sandbox';

export type ListKey = 'domains' | 'emails' | 'phones';

/** A refused outbound write's per-entry reasons (`errors: [{field: "allowlist.<list>", value, error}]`),
 *  by list — what the editor shows under each field. */
export function allowlistErrorsOf(e: unknown): Partial<Record<ListKey, string>> {
  if (!(e instanceof ConsoleApiError)) return {};
  const entries = (e.body as { errors?: unknown } | undefined)?.errors;
  if (!Array.isArray(entries)) return {};
  const out: Partial<Record<ListKey, string>> = {};
  for (const entry of entries) {
    if (!entry || typeof entry !== 'object') continue;
    const { field, value, error } = entry as { field?: unknown; value?: unknown; error?: unknown };
    const list = typeof field === 'string' ? field.replace(/^allowlist\./, '') : '';
    if (list !== 'domains' && list !== 'emails' && list !== 'phones') continue;
    const line = `${typeof value === 'string' && value ? `${value}: ` : ''}${typeof error === 'string' ? error : 'refused'}`;
    out[list] = out[list] ? `${out[list]}; ${line}` : line;
  }
  return out;
}

const FIELDS: { key: ListKey; label: string; placeholder: string; help: string }[] = [
  {
    key: 'domains',
    label: 'E-mail domains',
    placeholder: 'client.com',
    help: 'Every address at exactly this domain (subdomains need their own entry). Public mailbox domains are refused.',
  },
  {
    key: 'emails',
    label: 'E-mail addresses',
    placeholder: 'tester@example.org',
    help: 'Exact addresses, compared case-insensitively.',
  },
  {
    key: 'phones',
    label: 'Phone numbers',
    placeholder: '+14155550123',
    help: 'Exact numbers in E.164. SMS and WhatsApp reach them only once the sandbox has its own Twilio number.',
  },
];

export const EMPTY_ALLOWLIST: Required<SandboxAllowlist> = { domains: [], emails: [], phones: [] };

/** The three lists, never undefined. */
export function fullAllowlist(a: SandboxAllowlist | null | undefined): Required<SandboxAllowlist> {
  return { domains: a?.domains ?? [], emails: a?.emails ?? [], phones: a?.phones ?? [] };
}

/** Typed or pasted entries → trimmed, split, de-duplicated (case-insensitively). */
export function splitEntries(values: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of values) {
    for (const part of raw.split(/[\s,;]+/)) {
      const v = part.trim();
      if (!v || seen.has(v.toLowerCase())) continue;
      seen.add(v.toLowerCase());
      out.push(v);
    }
  }
  return out;
}

export function AllowlistEditor({
  value,
  onChange,
  ownDomains,
  disabled = false,
  errors,
}: {
  value: SandboxAllowlist | null | undefined;
  onChange: (next: Required<SandboxAllowlist>) => void;
  /** Our own domains: on every allowlist, never stored. */
  ownDomains: readonly string[];
  disabled?: boolean;
  /** The server's per-list reason, shown under the list. */
  errors?: Partial<Record<ListKey, string>>;
}) {
  const lists = fullAllowlist(value);
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {FIELDS.map((f) => (
        <Autocomplete<string, true, false, true>
          key={f.key}
          multiple
          freeSolo
          autoSelect
          options={[]}
          value={lists[f.key]}
          disabled={disabled}
          onChange={(_, next) => onChange({ ...lists, [f.key]: splitEntries(next) })}
          renderTags={(tags, getTagProps) =>
            tags.map((tag, index) => {
              const { key, ...rest } = getTagProps({ index });
              return <Chip key={key} size="small" label={tag} {...rest} />;
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={f.label}
              placeholder={lists[f.key].length === 0 ? f.placeholder : undefined}
              error={Boolean(errors?.[f.key])}
              helperText={
                errors?.[f.key] ??
                (f.key === 'domains' && ownDomains.length > 0
                  ? `${f.help} Always on the list: ${ownDomains.join(', ')}.`
                  : f.help)
              }
            />
          )}
        />
      ))}
    </Box>
  );
}
